import cn from "classnames";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import moment from "moment";
import { useState } from "react";
import { Button, Loader, Select, Switch, TableItemActionsControl } from "ui";

import AddHolidayModal from "./AddHolidayModal";
import { useDeliveryRules } from "./useDeliveryRules";

import styles from "./DeliveryRules.module.scss";

const DAYS: Array<{ dayOfWeek: number; label: string }> = [
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
  { dayOfWeek: 0, label: "Sunday" }
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = String(Math.floor(i / 2)).padStart(2, "0");
  const minute = i % 2 === 0 ? "00" : "30";
  const value = `${hour}:${minute}`;
  return { value, label: value };
});

const isWeekend = (dayOfWeek: number) => dayOfWeek === 6 || dayOfWeek === 0;

const DeliveryRules = () => {
  const {
    daysOff,
    isLoading,
    isSaving,
    setCutoffTime,
    toggleDeliveries,
    addHoliday,
    removeHoliday
  } = useDeliveryRules();

  const {
    value: isAddOpen,
    setTrue: openAdd,
    setFalse: closeAdd
  } = useBoolean(false);
  const [openedHolidayIndex, setOpenedHolidayIndex] = useState<number | null>(
    null
  );

  if (isLoading || !daysOff) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  const deliveriesOn = (dayOfWeek: number) => {
    if (dayOfWeek === 6) return !daysOff.weekend.isSaturdayOff;
    if (dayOfWeek === 0) return !daysOff.weekend.isSundayOff;
    return true;
  };

  const cutoffOf = (dayOfWeek: number) =>
    daysOff.cutoffSchedule.find((row) => row.dayOfWeek === dayOfWeek)
      ?.cutoffTime ?? "16:00";

  return (
    <ContentLayout title={t("Delivery Rules")}>
      <BoxShadowLayout className={styles.card}>
        <div className={styles.section}>
          <div className={styles.section__title}>
            {t("Weekly cutoff schedule")}
          </div>
          <div className={styles.section__subtitle}>
            {t(
              "Orders placed after the cutoff roll over to the next available delivery day."
            )}
          </div>
        </div>

        <div className={styles.schedule}>
          <div className={cn(styles.schedule__row, styles.schedule__head)}>
            <span>{t("Day")}</span>
            <span>{t("Deliveries")}</span>
            <span>{t("Same-Day Cutoff")}</span>
          </div>

          {DAYS.map(({ dayOfWeek, label }) => {
            const on = deliveriesOn(dayOfWeek);
            const cutoff = cutoffOf(dayOfWeek);
            const options = TIME_OPTIONS.some((o) => o.value === cutoff)
              ? TIME_OPTIONS
              : [{ value: cutoff, label: cutoff }, ...TIME_OPTIONS];

            return (
              <div
                key={dayOfWeek}
                className={cn(styles.schedule__row, {
                  [styles.schedule__rowOff]: !on
                })}
              >
                <span className={styles.schedule__day}>{t(label)}</span>

                <div className={styles.schedule__deliveries}>
                  <Switch
                    checked={on}
                    disabled={isSaving || !isWeekend(dayOfWeek)}
                    onChange={(e) =>
                      toggleDeliveries(dayOfWeek, e.target.checked)
                    }
                  />
                  <span>{on ? t("On") : t("Off")}</span>
                </div>

                <div className={styles.schedule__cutoff}>
                  <Select
                    value={cutoff}
                    disabled={!on || isSaving}
                    options={options}
                    onChange={(value) =>
                      setCutoffTime(
                        dayOfWeek,
                        Array.isArray(value) ? value[0] : value
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </BoxShadowLayout>

      <BoxShadowLayout className={styles.card}>
        <div className={styles.holidaysHeader}>
          <div className={styles.section}>
            <div className={styles.section__title}>
              {t("Holidays & blackout dates")}
            </div>
            <div className={styles.section__subtitle}>
              {t(
                "One-off overrides that beat the weekly schedule - public holidays, inventory days or weather closures."
              )}
            </div>
          </div>
          <Button title="Add date" onClick={openAdd} />
        </div>

        <div className={styles.holidays}>
          <div className={cn(styles.holidays__row, styles.holidays__head)}>
            <span>{t("Name")}</span>
            <span>{t("Date")}</span>
            <span />
            <span />
          </div>

          {daysOff.holidays.length === 0 ? (
            <div className={styles.holidays__empty}>
              {t("No holidays added yet")}
            </div>
          ) : (
            daysOff.holidays.map((holiday, index) => (
              <div key={holiday.id} className={styles.holidays__row}>
                <span className={styles.holidays__name}>{holiday.name}</span>
                <span>{moment(holiday.from).format("D MMMM")}</span>
                <span className={styles.holidays__badge}>
                  {t("No delivery")}
                </span>
                <TableItemActionsControl
                  rowIndex={index}
                  openedItemOptionsIndex={openedHolidayIndex}
                  setOpenedItemOptionsIndex={setOpenedHolidayIndex}
                  actionsList={[
                    {
                      label: t("Delete"),
                      labelColor: "#FF0033",
                      onClick: () => removeHoliday(holiday.id)
                    }
                  ]}
                />
              </div>
            ))
          )}
        </div>
      </BoxShadowLayout>

      <AddHolidayModal
        isOpen={isAddOpen}
        isSaving={isSaving}
        onClose={closeAdd}
        onSubmit={(name, date) => {
          addHoliday(name, date);
          closeAdd();
        }}
      />
    </ContentLayout>
  );
};

export default DeliveryRules;
